import { Fragment, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useBack } from '../lib/back.js';
import { alpha, theme } from '../theme.js';
import { useAccess } from '../context/AuthContext.js';
import { PageShell } from '../components/PageShell.js';
import { Icon } from '../components/Icon.js';
import { HELP_SECTIONS, WHO_DOES_WHAT, type HelpTopic } from '../lib/helpTopics.js';

const CONTACT = 'enquiries@mustertap.com';

/**
 * How-to guide at /help. Topics are questions people actually ask; sections
 * for team leaders and admins only show to them, but every member sees what
 * leaders and admins can do for them. /help#<topic id> opens one topic.
 */
export function HelpPage() {
  const back = useBack('/');
  const { me, isAdmin, ledTeam } = useAccess();
  const { hash } = useLocation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<Set<string>>(() => new Set());

  const unitName = me?.orgs.find((o) => o.id === me.orgId)?.name;
  const roleText =isAdmin ? 'an admin' : ledTeam ? `${ledTeam.role === 'deputy' ? 'deputy' : 'team'} leader of ${ledTeam.name}` : 'a member';
  const sections = HELP_SECTIONS.filter((s) =>
    s.audience === 'everyone' || (s.audience === 'leader' && (isAdmin || !!ledTeam)) || (s.audience === 'admin' && isAdmin));

  const q = query.trim().toLowerCase();
  const shown = useMemo(() => sections
    .map((s) => ({ ...s, topics: q ? s.topics.filter((t) => searchText(t).includes(q)) : s.topics }))
    .filter((s) => s.topics.length), [sections, q]);

  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    setOpen((prev) => new Set(prev).add(id));
    requestAnimationFrame(() => document.getElementById(`help-${id}`)?.scrollIntoView({ block: 'start' }));
  }, [hash]);

  const toggle = (id: string, isOpen: boolean) => setOpen((prev) => {
    const next = new Set(prev);
    if (isOpen) next.add(id); else next.delete(id);
    return next;
  });

  return (
    <PageShell noNav>
      <style>{CSS}</style>
      <div style={{
        padding: '12px 16px', background: theme.surface, borderBottom: `1px solid ${theme.border}`,
        display: 'flex', alignItems: 'center', gap: 10, position: 'sticky', top: 0, zIndex: 5,
      }}>
        <button onClick={back} style={iconBtn} aria-label="Back">
          <Icon name="back" size={18} />
        </button>
        <div style={{ fontSize: 17, fontWeight: 700, color: theme.text }}>Help</div>
      </div>

      <div className="hp" style={{ flex: 1, overflowY: 'auto', background: theme.bg, padding: '16px 16px 40px' }}>
        <h1 style={{ fontSize: 22, margin: '4px 0 6px', color: theme.text, letterSpacing: -0.4 }}>How do I…?</h1>
        <p style={{ margin: '0 0 14px', fontSize: 14, color: theme.textDim, lineHeight: 1.5 }}>
          {me ? <>You’re {roleText}{unitName ? <> in {unitName}</> : null}. </> : null}
          Tap a question to see the steps.
        </p>

        <div style={{ position: 'relative', marginBottom: 16 }}>
          <Icon name="search" size={16} style={{ position: 'absolute', left: 12, top: 13, color: theme.textMuted }} />
          <input
            type="search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search help, e.g. cover, leave, swap"
            aria-label="Search help"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '11px 12px 11px 36px', borderRadius: 12,
              border: `1px solid ${theme.border}`, background: theme.surface, color: theme.text,
              fontSize: 15, fontFamily: theme.font,
            }}
          />
        </div>

        {!q && <WhoDoesWhat />}

        {shown.map((s) => (
          <section key={s.id} style={{ marginTop: 22 }}>
            <h2 style={{
              display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 0.5, color: theme.textMuted, margin: '0 0 8px 2px',
            }}>
              <Icon name={s.icon} size={15} />
              {s.title}
              {s.audience !== 'everyone' && (
                <span style={badge}>{s.audience === 'admin' ? 'Only admins see this' : 'Only leaders and admins see this'}</span>
              )}
            </h2>
            <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 14, overflow: 'hidden' }}>
              {s.topics.map((t, i) => (
                <details
                  key={t.id} id={`help-${t.id}`}
                  open={!!q || open.has(t.id)}
                  onToggle={(e) => { if (!q) toggle(t.id, e.currentTarget.open); }}
                  style={{ borderTop: i ? `1px solid ${theme.border}` : 'none', scrollMarginTop: 70 }}
                >
                  <summary>
                    <span style={{ flex: 1 }}>{t.q}</span>
                    <Icon name="chevronD" size={18} style={{ color: theme.textMuted, flexShrink: 0 }} />
                  </summary>
                  <TopicBody t={t} />
                </details>
              ))}
            </div>
          </section>
        ))}

        {q && !shown.length && (
          <p style={{ fontSize: 14, color: theme.textDim, marginTop: 8 }}>
            Nothing matches “{query.trim()}”. Try a simpler word, like <em>cover</em> or <em>check</em>.
          </p>
        )}

        <div style={{ ...card, marginTop: 28 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.text, marginBottom: 6 }}>Still stuck?</div>
          <p style={{ margin: 0, fontSize: 14, color: theme.textDim, lineHeight: 1.55 }}>
            Ask your team leader or a unit admin. They can fix most things for you. If the app itself isn’t
            working, email <a href={`mailto:${CONTACT}`} style={{ color: theme.accent }}>{CONTACT}</a>.
          </p>
        </div>
      </div>
    </PageShell>
  );
}

function TopicBody({ t }: { t: HelpTopic }) {
  return (
    <div style={{ padding: '0 16px 16px', fontSize: 15, lineHeight: 1.55, color: theme.text }}>
      {t.intro && <p style={{ margin: '0 0 10px' }}>{rich(t.intro)}</p>}
      {t.steps && (
        <ol className="hp-steps">
          {t.steps.map((step, i) => <li key={i}>{rich(step)}</li>)}
        </ol>
      )}
      {t.tip && (
        <div style={{ ...note, background: theme.surface2, borderColor: theme.border }}>
          <div style={noteLabel}>Good to know</div>
          {rich(t.tip)}
        </div>
      )}
      {t.others && (
        <div style={{ ...note, background: alpha(theme.accent, 7), borderColor: alpha(theme.accent, 25) }}>
          <div style={{ ...noteLabel, color: theme.accent, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="people" size={14} /> Team leaders and admins
          </div>
          {rich(t.others)}
        </div>
      )}
    </div>
  );
}

function WhoDoesWhat() {
  return (
    <details className="hp-who" style={card}>
      <summary style={{ padding: 0 }}>
        <span style={{ flex: 1 }}>
          <span style={{ display: 'block', fontSize: 15, fontWeight: 700, color: theme.text }}>Who can do what</span>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 400, color: theme.textMuted, marginTop: 2 }}>
            What you can do yourself, and who to ask for the rest
          </span>
        </span>
        <Icon name="chevronD" size={18} style={{ color: theme.textMuted, flexShrink: 0 }} />
      </summary>
      <div style={{ marginTop: 12 }}>
        {WHO_DOES_WHAT.map((g, i) => (
          <div key={g.who} style={{ paddingTop: i ? 12 : 0, marginTop: i ? 12 : 0, borderTop: i ? `1px solid ${theme.border}` : 'none' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.text, marginBottom: 4 }}>{g.who}</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, color: theme.textDim, lineHeight: 1.55 }}>
              {g.what.map((w) => <li key={w}>{w}</li>)}
            </ul>
          </div>
        ))}
      </div>
    </details>
  );
}

/** `**Button**` → bold, so button names stand out in the steps. */
function rich(text: string) {
  return text.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 ? <b key={i}>{part}</b> : <Fragment key={i}>{part}</Fragment>);
}

const searchText = (t: HelpTopic) =>
  [t.q, t.intro, ...(t.steps ?? []), t.tip, t.others].join(' ').replace(/\*\*/g, '').toLowerCase();

const CSS = `
.hp summary {
  list-style: none; cursor: pointer; display: flex; align-items: center; gap: 10px;
  padding: 15px 16px; font-size: 15px; font-weight: 600; color: ${theme.text};
}
.hp summary::-webkit-details-marker { display: none; }
.hp details[open] > summary svg:last-child { transform: rotate(180deg); }
.hp-who summary { padding: 0; }
.hp-steps { margin: 0; padding-left: 22px; }
.hp-steps li { margin: 0 0 8px; padding-left: 4px; }
.hp-steps li::marker { font-weight: 700; color: ${theme.accent}; }
`;

const iconBtn: React.CSSProperties = {
  width: 36, height: 36, border: `1px solid ${theme.border}`,
  background: theme.surface2, color: theme.text, borderRadius: 10,
  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
};

const card: React.CSSProperties = {
  background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 16,
};

const note: React.CSSProperties = {
  marginTop: 12, padding: '10px 12px', borderRadius: 10, border: '1px solid', fontSize: 14, lineHeight: 1.5,
};

const noteLabel: React.CSSProperties = {
  fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: theme.textMuted, marginBottom: 3,
};

const badge: React.CSSProperties = {
  marginLeft: 'auto', fontSize: 10, fontWeight: 700, letterSpacing: 0.3, textTransform: 'none',
  padding: '2px 8px', borderRadius: 999, background: alpha(theme.accent, 10), color: theme.accent,
};
